/**
 * Карточка сайта v2 в «Сайтах» раздела EO «Виджет» (01.11, ТЗ 1.17 EMB-019 ч. 1): ключ (публичен, SEC-016) и перевыпуск (старый
 * принимается ещё 7 дней), v2 вкл/выкл (замок «МК переведён», предел списка Origin — отказы сервера показываются словами), отзыв
 * ключа прежним переключателем, config по полям (WidgetSiteConfig), тексты согласий (WidgetLegal), журнал изменений (audit_log
 * сайта). Каждое изменение пишет сервер в аудит; адреса прокладки отсюда не меняются. «Отозвать» и «Вернуть» шлют нужное
 * состояние ключа (keyToggle): ключ уже в нём — отказ «обновите», а не переворот назад; текст — по ответу сервера; после любого
 * действия, удачного или нет, карточка перечитывается (ревью b235d4e4).
 */
import { useState } from "react";
import { api } from "../../../api/client";
import { ConfirmModal } from "../../../components/shared";
import { copyText } from "../../../lib/clipboard";
import { BASE, failText, useLoad } from "./widgetApi";
import { orenTime } from "./widgetFmt";
import { keyToggle, keyToggleText } from "./widgetPick";
import type { JournalRow, SiteV2 } from "./widgetTypes";
import { WidgetLegal } from "./WidgetLegal";
import { WidgetSiteConfig } from "./WidgetSiteConfig";
import css from "./widget.module.css";

type Ask = "reissue" | "revoke" | "restore" | null;

export function WidgetSiteCard({ id, onBack, onInstall }: { id: number; onBack: () => void; onInstall: () => void }) {
  const v2 = useLoad<SiteV2>(`${BASE}/${id}/v2`);
  const journal = useLoad<{ rows: JournalRow[] }>(`${BASE}/${id}/v2/journal`);
  const keyRow = useLoad<{ sites: { id: number; key: string }[] }>(`${BASE}/v2/overview`);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");
  const [ask, setAsk] = useState<Ask>(null);
  const key = keyRow.data?.sites.find((s) => s.id === id)?.key ?? "";
  const reload = () => { v2.reload(); journal.reload(); keyRow.reload(); };

  const run = async (what: string, fn: () => Promise<string>) => {
    setBusy(true); setToast("");
    try { setToast(await fn()); } catch (e) { setToast(failText(e, what)); }
    reload();
    setBusy(false);
  };
  const toggleV2 = (enabled: boolean) => run(enabled ? "Не удалось включить v2" : "Не удалось выключить v2", async () => {
    await api(`${BASE}/${id}/v2/enabled`, { method: "PUT", body: { enabled } });
    return enabled ? "v2 включён: сайт попадёт в список доменов прокладки в течение 5 минут" : "v2 выключен";
  });
  const confirm = () => {
    const a = ask;
    setAsk(null);
    if (a === "reissue") void run("Не удалось перевыпустить ключ", async () => {
      const r = await api<{ prev_key_until: string | null }>(`${BASE}/${id}/v2/reissue`, { method: "POST", body: {} });
      return `Ключ перевыпущен. Прежний принимается до ${orenTime(r.prev_key_until ? `${r.prev_key_until.replace(" ", "T")}Z` : null)} — поменяйте код установки на сайте.`;
    });
    else if (a === "revoke" || a === "restore") void run(a === "revoke" ? "Не удалось отозвать ключ" : "Не удалось вернуть ключ", async () => {
      const t = keyToggle(a), r = await api<{ active?: boolean }>(`${BASE}/${id}/toggle`, { method: "PUT", body: t.body });
      return keyToggleText(t.body.active, r.active);
    });
  };
  const copyKey = async () => setToast((await copyText(key)) ? "Ключ скопирован" : "Не удалось скопировать — выделите ключ и скопируйте вручную");

  const d = v2.data;
  return (
    <div className={css.card}>
      <div className={css.head}>
        <span className={css.title}>{d?.site.domain ?? `Сайт №${id}`}</span>
        <div className={css.line}>
          <button type="button" className={css.ghost} onClick={onBack}>← К списку</button>
          <button type="button" className={css.ghost} disabled={v2.busy} onClick={reload}>Обновить</button>
        </div>
      </div>
      {v2.err ? <div className={css.err}>{v2.err}</div> : null}
      {toast ? <div className={css.toast} role="status">{toast}</div> : null}
      <div className={css.block}>
        <div className={css.sub}>Ключ сайта (публичный: он же в data-site кода установки)</div>
        <pre className={css.code}>{key || "…"}</pre>
        <div className={css.line}>
          <button type="button" className={css.ghost} disabled={!key} onClick={() => void copyKey()}>Скопировать ключ</button>
          <button type="button" className={css.ghost} disabled={busy || !d?.config} onClick={() => setAsk("reissue")}>Перевыпустить</button>
          <button type="button" className={css.ghost} onClick={onInstall}>Код установки</button>
          {d ? <button type="button" className={css.danger} disabled={busy} onClick={() => setAsk(d.site.active ? "revoke" : "restore")}>{d.site.active ? "Отозвать ключ" : "Вернуть ключ"}</button> : null}
        </div>
        {d?.prev_key_until ? <span className={css.muted}>Прежний ключ принимается до {orenTime(`${d.prev_key_until.replace(" ", "T")}Z`)}</span> : null}
      </div>
      {d ? (
        <div className={css.line}>
          <span className={`${css.badge} ${d.enabled ? css.ok : css.none}`}>{d.enabled ? "v2 включён" : "v2 выключен"}</span>
          <button type="button" className={d.enabled ? css.ghost : css.btn} disabled={busy || !d.config} onClick={() => void toggleV2(!d.enabled)}>
            {d.enabled ? "Выключить v2" : "Включить v2"}
          </button>
          {!d.config ? <span className={css.muted}>сначала сохраните конфиг</span> : !d.mk_migrated && !d.enabled ? <span className={css.warnLine}>замок: не отмечено «МК переведён»</span> : null}
        </div>
      ) : null}
      {d ? <WidgetSiteConfig key={id} id={id} config={d.config} version={d.config_version} onSaved={reload} /> : null}
      {d ? <WidgetLegal id={id} texts={d.legal} onSaved={reload} /> : null}
      <div className={css.block}>
        <div className={css.sub}>Журнал изменений</div>
        {journal.err ? <div className={css.err}>{journal.err}</div> : null}
        {journal.data && !journal.data.rows.length ? <div className={css.muted}>записей нет</div> : null}
        {journal.data?.rows.map((r, i) => (
          <div key={`${r.at}-${i}`} className={css.row}>{orenTime(r.at)} · {r.who ?? "система"} · {r.text || r.action}</div>
        ))}
      </div>
      {ask ? (
        <ConfirmModal tone={ask === "restore" ? "warn" : "danger"} actionLabel={ask === "reissue" ? "Перевыпустить" : ask === "revoke" ? "Отозвать" : "Вернуть"}
          title={ask === "reissue" ? "Перевыпустить ключ сайта?" : ask === "revoke" ? "Отозвать ключ сайта?" : "Вернуть ключ сайта?"}
          onAction={confirm} onCancel={() => setAsk(null)}>
          {ask === "reissue" ? "Новый ключ действует сразу, прежний — ещё 7 дней. За это время замените код установки на сайте."
            : ask === "revoke" ? "Сайт сразу получит отказ: виджет покажет аварийную панель с контактами. Заявки с сайта перестанут приходить."
              : "Ключ снова начнёт работать; если v2 включён — сайт вернётся в список доменов прокладки."}
        </ConfirmModal>
      ) : null}
    </div>
  );
}
